import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuthStore } from '../../stores/authStore';
import { SECTIONS, FOOTER_ITEMS, type NavItem } from './navCatalog';

/** Hamburger button + slide-in drawer with the full sidebar nav for
 * small screens. Mounts at the top of AppShell; hidden on md+. */
export default function MobileNavDrawer() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();
  const isAdmin = useAuthStore((s) => s.user?.is_admin ?? false);
  const logout = useAuthStore((s) => s.logout);

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  const renderItem = (it: NavItem) => {
    const active = loc.pathname === it.to || loc.pathname.startsWith(`${it.to}/`);
    const label = it.label.startsWith('nav.') ? (t(it.label) || it.label) : it.label;
    return (
      <Box
        key={it.to}
        onClick={() => go(it.to)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2, py: 1.25,
          cursor: 'pointer',
          borderInlineStart: active ? '3px solid' : '3px solid transparent',
          borderColor: active ? 'primary.main' : 'transparent',
          bgcolor: active ? 'action.hover' : 'transparent',
        }}
      >
        <Box sx={{ color: active ? 'primary.main' : 'text.secondary' }}>{it.icon}</Box>
        <Typography sx={{
          fontSize: 14,
          fontWeight: active ? 700 : 500,
          color: active ? 'primary.main' : 'text.primary',
        }}>
          {label}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <IconButton
        size="small"
        sx={{ display: { xs: 'inline-flex', md: 'none' } }}
        onClick={() => setOpen(true)}
      >
        <MenuIcon />
      </IconButton>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: '85vw', maxWidth: 360 } }}
      >
        <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography sx={{ fontWeight: 800, fontSize: 18, flexGrow: 1 }}>תפריט</Typography>
          <IconButton size="small" onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 1 }}>
          {SECTIONS.map((s) => {
            const visible = s.items.filter((it) => !it.adminOnly || isAdmin);
            if (visible.length === 0) return null;
            return (
              <Box key={s.title} sx={{ mb: 1 }}>
                <Typography sx={{
                  px: 2, py: 0.5,
                  fontSize: 11, fontWeight: 700,
                  color: 'text.disabled',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '0.06em',
                }}>
                  {s.title}
                </Typography>
                {visible.map(renderItem)}
              </Box>
            );
          })}
          <Divider sx={{ my: 1 }} />
          {FOOTER_ITEMS.filter((it) => !it.adminOnly || isAdmin).map(renderItem)}
          <Divider sx={{ my: 1 }} />
          <Box
            onClick={() => { logout(); setOpen(false); navigate('/login'); }}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 2, py: 1.25,
              cursor: 'pointer',
              color: 'error.main',
            }}
          >
            <LogoutIcon />
            <Typography sx={{ fontWeight: 600 }}>התנתק</Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
